import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { langs } from '../i18n/config';
import api from '../service/axiosInstance';
import _ from 'lodash';
import {
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { useCookies } from 'react-cookie';
import { useUser } from '../contexts/UserContext';
import { sendGAEvent, formatTimestamp } from '../analyticsEvents';

import Toast from '../components/Toast';

export default function CheckoutForm() {
  const isShahidFlow = process.env.REACT_APP_FLOW_TYPE === 'shahid';
  const { userData } = useUser();

  const { t, i18n } = useTranslation('common');
  const [cookies, removeCookie] = useCookies();

  const { lang } = useParams();

  const prefix = langs.lastIndexOf(lang) >= 0 ? `/${lang}` : '';

  const [toasts, setToasts] = useState([]);

  const stripe = useStripe();
  const elements = useElements();

  const navigate = useNavigate();

  const [nextBtnLocked, setNextBtnLocked] = useState(false);

  const handleCheckout = () => {
    if (!stripe || !elements) return;
    setNextBtnLocked(true);

    const errorToasts = (error) => {
      setNextBtnLocked(false);

      const data = error.response?.data || error;

      if (data.detail) {
        const detail = data.detail;
        let details = [];

        if (_.isArray(detail)) {
          details = _.map(detail, (item) => {
            const loc = _.last(item.loc);
            const msg = item.msg;
            return { msg: t(`${loc}: ${msg}`), type: 'error' };
          });
        } else {
          details = [{ msg: t(detail), type: 'error' }];
        }

        setToasts((state) => state.concat(details));
      } else if (data.message) {
        setToasts((state) =>
          state.concat([{ msg: t(data.message), type: 'error' }])
        );
      }
    };

    const stripeConfirmSetup = () => {
      stripe
        .confirmSetup({
          elements,
          redirect: 'if_required',
        })
        .then(({ setupIntent, error }) => {
          if (setupIntent) {
            subscriptionsSetupConfirm(setupIntent.id);
          }
          if (error) {
            errorToasts({
              response: { data: { message: error.message } },
            });
          }
        })
        .catch((e) => {
          console.log({ e });
        });
    };

    const subscriptionsSetupConfirm = async (setupIntentId) => {
      await api
        .post('/api/v1/subscriptions/stripe/setup/confirm', {
          setup_intent_id: setupIntentId,
        })
        .catch((error) => {
          errorToasts(error);
        });
      redeemCode();
    };

    const redeemCode = () => {
      const setupIntentSecret = cookies['setupIntentSecret'];
      const currentTimestamp = Date.now();

      api
        .post('/api/v1/coupons/redeem/stripe', {
          code: cookies['couponCode'],
        })
        .then(() => {
          const message = t('place_order_success');
          setToasts((state) =>
            state.concat([{ msg: message, type: 'success' }])
          );

          sendGAEvent('purchase_completed', {
            timestamp: formatTimestamp(currentTimestamp),
            email: userData?.email,
            code_used: setupIntentSecret,
          });

          removeCookie('setupIntentSecret');
          const completionUrl = prefix + '/download';
          navigate(completionUrl);
        })
        .catch((error) => {
          sendGAEvent('purchase_error', {
            timestamp: formatTimestamp(currentTimestamp),
            email: userData?.email,
            code_used: setupIntentSecret,
            error_message:
              error.response?.data?.message || error.message || 'Unknown error',
          });

          errorToasts(error);
        });
    };

    stripeConfirmSetup();
  };

  const billingFrequency = cookies['billingFrequency'] ?? 'year';
  const currencyAmount = (cookies['currencyAmount'] ?? 0) / 100;
  const currencyName = cookies['currencyName'] ?? 0;
  const durationInDays = cookies['durationInDays'] ?? 0;

  const currency = (currencyName || 'usd').toUpperCase();

  const formattedCurrencyFull = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(currencyAmount);

  const formattedCurrencyToday = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(0);

  const trialEndDate = new Date();
  trialEndDate.setDate(trialEndDate.getDate() + durationInDays);
  const trialLongEndDate = Intl.DateTimeFormat(i18n.language, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(trialEndDate);

  return (
    <>
      <div className="flex flex-col sm:flex-row">
        <aside>
          <div className="flex items-center justify-center p-4 m-2">
            <PaymentElement />
          </div>
        </aside>
        <aside>
          <div className="flex m-2 mb-0 shadow-xl card bg-base-100">
            <div
              style={{
                backgroundImage: 'url(/images/checkout-header.png)',
                backgroundSize: 'contain',
                height: 60,
                marginBottom: 15,
                backgroundRepeat: 'repeat-x',
              }}
            >
              <h4
                className={`text-lg font-bold m-3 ${isShahidFlow ? 'text-accent' : 'text-primary'}`}
              >
                {t('your_plan')}
              </h4>
            </div>
            <div className="flex justify-between px-4">
              <aside>
                <h4
                  className={`mb-2 text-sm ${isShahidFlow ? 'text-accent' : 'text-primary'}`}
                >
                  {t('playkids_' + billingFrequency + 'ly_plan')}
                </h4>
                <h4 className="mb-2 text-sm">
                  {durationInDays} {t('days_trial')}
                </h4>
              </aside>
              <aside>
                <p
                  className={`mb-2 text-sm text-right ${isShahidFlow ? 'text-accent' : 'text-primary'}`}
                >
                  {formattedCurrencyFull}
                  <span className="text-sm">/{t(billingFrequency)}</span>
                </p>
                <p className="mb-2 text-sm text-right">{t('Free')}</p>
              </aside>
            </div>
            <hr className="mx-2 mb-2 border-black" />
            <div className="flex justify-between px-4">
              <aside>
                <h4 className="mb-2 text-sm font-bold">
                  {t('total_due_today')}
                </h4>
                <h4 className="mb-2 text-sm">
                  {t('total_due_after_trial')}
                  <span style={{ fontSize: 10 }} className="mx-1">
                    ({trialLongEndDate})
                  </span>
                </h4>
              </aside>
              <aside>
                <p className="mb-2 text-sm font-bold text-right ">
                  {formattedCurrencyToday}
                </p>
                <p className="mb-2 text-sm text-right ">
                  {formattedCurrencyFull}
                  <span className="text-sm">/{t(billingFrequency)}</span>
                </p>
              </aside>
            </div>
            <div>
              <br />
              <div className="mx-3 mb-4 text-xs">
                <span>{t('remind_warning')}</span>
                <br />
                <span>
                  {t('remind_warning_2')}{' '}
                  <a
                    className="link"
                    href={t('terms_of_use_link')}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t('terms_of_use')}
                  </a>{' '}
                  {t('apply')}.
                </span>
              </div>
            </div>
          </div>
          <div className="flex justify-center my-4">
            <button
              className={
                'btn btn-outline rounded-full text-md uppercase mr-2 ' +
                (isShahidFlow ? 'btn-accent' : 'btn-primary')
              }
              onClick={() => {
                navigate('/redeem');
              }}
            >
              {t('go_back')}
            </button>
            <button
              className={
                'btn btn-primary rounded-full text-md uppercase ' +
                (isShahidFlow ? 'btn-accent' : 'btn-primary')
              }
              disabled={nextBtnLocked}
              onClick={handleCheckout}
            >
              {t('proceed')}
            </button>
          </div>
        </aside>
      </div>

      <div className="z-50 toast toast-top toast-end">
        {_.map(toasts, ({ msg, type }, index) => (
          <Toast
            msg={msg}
            type={type}
            key={msg + index}
            index={index}
            total={toasts.length}
          />
        ))}
      </div>
    </>
  );
}
